import type { CourseProfile } from '../types/courseProfile';

const LEGACY_PROFILES_KEY = 'sala_course_profiles';
const LEGACY_ACTIVE_KEY = 'sala_active_course_profile_id';

let storageOwnerUid: string | null = null;

export function setCourseStorageOwner(uid: string | null): void {
  storageOwnerUid = uid;
}

function profilesKey(): string {
  return storageOwnerUid
    ? `sala_course_profiles_${storageOwnerUid}`
    : LEGACY_PROFILES_KEY;
}

function activeKey(): string {
  return storageOwnerUid
    ? `sala_active_course_profile_id_${storageOwnerUid}`
    : LEGACY_ACTIVE_KEY;
}

function canUseStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function readProfiles(): CourseProfile[] {
  if (!canUseStorage()) return [];
  try {
    const raw = localStorage.getItem(profilesKey());
    if (!raw) {
      // Migración one-shot: si hay uid y solo existen perfiles legacy, no los movemos
      // automáticamente (evita filtrar datos de otro docente). Empezamos limpio por uid.
      return [];
    }
    const parsed = JSON.parse(raw) as CourseProfile[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeProfiles(profiles: CourseProfile[]): void {
  if (!canUseStorage()) return;
  localStorage.setItem(profilesKey(), JSON.stringify(profiles));
}

export function createCourseProfileId(): string {
  return `course_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function getCourseProfiles(): CourseProfile[] {
  return readProfiles();
}

export function getCourseProfileById(id: string): CourseProfile | null {
  return readProfiles().find((profile) => profile.id === id) ?? null;
}

export function saveCourseProfile(profile: CourseProfile): CourseProfile {
  const profiles = readProfiles();
  const idx = profiles.findIndex((item) => item.id === profile.id);
  if (idx >= 0) profiles[idx] = profile;
  else profiles.push(profile);
  writeProfiles(profiles);

  if (canUseStorage()) {
    const activeId = localStorage.getItem(activeKey());
    if (!activeId) {
      localStorage.setItem(activeKey(), profile.id);
    }
  }

  return profile;
}

export function getActiveCourseProfile(): CourseProfile | null {
  if (!canUseStorage()) return null;
  const activeId = localStorage.getItem(activeKey());
  if (!activeId) return null;
  return readProfiles().find((profile) => profile.id === activeId) ?? null;
}

export function setActiveCourseProfile(id: string): CourseProfile | null {
  if (!canUseStorage()) return null;
  const profile = readProfiles().find((item) => item.id === id) ?? null;
  if (!profile) return null;
  localStorage.setItem(activeKey(), id);
  return profile;
}

export function deleteCourseProfile(id: string): void {
  if (!canUseStorage()) return;
  const profiles = readProfiles().filter((item) => item.id !== id);
  writeProfiles(profiles);

  const activeId = localStorage.getItem(activeKey());
  if (activeId === id) {
    if (profiles.length > 0) {
      localStorage.setItem(activeKey(), profiles[0].id);
    } else {
      localStorage.removeItem(activeKey());
    }
  }
}

export function saveManyCourseProfiles(profiles: CourseProfile[]): void {
  const existing = readProfiles();
  const byId = new Map(existing.map((item) => [item.id, item]));
  for (const profile of profiles) {
    byId.set(profile.id, profile);
  }
  writeProfiles([...byId.values()]);
  if (canUseStorage() && !localStorage.getItem(activeKey()) && profiles[0]) {
    localStorage.setItem(activeKey(), profiles[0].id);
  }
}
